type Comparable = number | string | bigint | Date;

interface Array<T> {
    /**
     * Checks if any item in the array matches the predicate
     * @param predicate A boolean function to match to each item
     */
    any(predicate: (item: T) => boolean): boolean;

    /**
     * Checks if the specified item is included in the array
     * @param item The item to check for
     */
    contains(item: T): boolean;

    /**
     * Returns an array which excludes the specified item
     * @param item The item to exclude
     */
    except(item: T): Array<T>;

    /**
     * Returns an array which excludes the items which match the predicate
     * @param predicate A boolean function to match to each item to exclude
     */
    exceptAny(predicate: (item: T) => boolean): Array<T>;

    /**
     * Returns an array sorted by the selector
     * @param selector The selector to sort by
     * @param descending Whether to reverse the sort order
     */
    orderBy(selector: (item: T) => Comparable): Array<T>;
    orderBy(selector: (item: T) => Comparable, descending?: boolean): Array<T>;

    /**
     * Returns a page of an array
     * @param pageNumber The page number to return
     * @param pageSize The size of each page
     */
    page(pageNumber: number, pageSize: number): Array<T>;

    /**
     * Returns an array excluding the specified number of items from the begining
     * @param total The number of items to exclude
     */
    skip(total: number): Array<T>;

    /**
     * Returns the specified number of items from the top of the array
     * @param total The number of items to return
     */
    take(total: number): Array<T>;
}

Array.prototype.any = function <T>(predicate: (item: T) => boolean): boolean {
    return (this.findIndex(predicate) > -1);
};

Array.prototype.contains = function <T>(item: T) {
    return this.indexOf(item) > -1;
};

Array.prototype.except = function <T>(item: T) {
    if (!this.contains(item)) return this;

    const copy = [...this];

    copy.splice(this.indexOf(item), 1);

    return copy;
};

Array.prototype.exceptAny = function <T>(predicate: (item: T) => boolean): Array<T> {
    const copy: Array<T> = [];

    for (const currentItem of this) {
        if (!predicate(currentItem)) {
            copy.push(currentItem);
        }
    }

    return copy;
};

Array.prototype.orderBy = function <T>(selector: (item: T) => Comparable, descending: boolean = false): Array<T> {
    return this.slice().sort((a, b) => {
        const aVal = selector(a);
        const bVal = selector(b);

        // Handle string comparison
        if (typeof aVal === "string" && typeof bVal === "string") {
            return descending
                ? bVal.localeCompare(aVal)
                : aVal.localeCompare(bVal);
        }

        // Handle number (or other comparable types)
        return descending
            ? (bVal > aVal ? 1 : bVal < aVal ? -1 : 0)
            : (aVal > bVal ? 1 : aVal < bVal ? -1 : 0);
    });
};

Array.prototype.page = function <T>(pageNumber: number, pageSize: number): Array<T> {
    if (pageSize === 0) return this;
    return this.skip((pageNumber - 1) * pageSize).take(pageSize);
};

Array.prototype.skip = function <T>(total: number): Array<T> {
    if (total > this.length) return [];
    return this.slice(total, this.length);
};

Array.prototype.take = function <T>(total: number): Array<T> {
    if (this.length <= total) return [...this];
    return this.slice(0, total);
};
